import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { api, isAdmin, clearAdminPw, getActiveClubId, setActiveClubId } from "../lib/api";
import { Target, LayoutDashboard, ListOrdered, CalendarDays, ClipboardList, BarChart3, Settings, LogOut, Crown } from "lucide-react";

const navLinks = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/tabelle", label: "Tabelle", icon: ListOrdered, testid: "nav-tabelle" },
  { to: "/spielplan", label: "Spielplan", icon: CalendarDays, testid: "nav-spielplan" },
  { to: "/erfassen", label: "Erfassen", icon: ClipboardList, testid: "nav-erfassen" },
  { to: "/statistik", label: "Statistik", icon: BarChart3, testid: "nav-statistik" },
  { to: "/rekorde", label: "Rekorde", icon: Crown, testid: "nav-rekorde" },
  { to: "/admin", label: "Admin", icon: Settings, testid: "nav-admin" },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(isAdmin());
  const visibleNavLinks = navLinks.filter((link) => link.to !== "/erfassen" || admin);
  const [logo, setLogo] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [activeClub, setActiveClub] = useState(null);

  useEffect(() => {
    api.listClubs().then((items) => {
      setClubs(items);
      const selected = items.find((club) => club.id === getActiveClubId()) || items[0] || null;
      if (selected) setActiveClubId(selected.id);
      setActiveClub(selected);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const refreshAdmin = () => setAdmin(isAdmin());
    window.addEventListener("admin-auth-changed", refreshAdmin);
    return () => window.removeEventListener("admin-auth-changed", refreshAdmin);
  }, []);

  useEffect(() => {
    if (!activeClub) return;
    document.documentElement.style.setProperty("--dc-primary", activeClub.primary_color);
    document.documentElement.style.setProperty("--dc-secondary", activeClub.secondary_color);
    api.activeSeason().then((s) => setLogo(activeClub.logo_url || s?.custom_logo_url || null)).catch(() => setLogo(activeClub.logo_url || null));
  }, [activeClub]);

  const changeClub = (event) => {
    const selected = clubs.find((club) => club.id === event.target.value);
    if (!selected) return;
    setActiveClubId(selected.id);
    window.location.assign("/");
  };

  const logout = () => {
    clearAdminPw();
    navigate("/");
    window.location.reload();
  };

  if (!activeClub) return <div className="min-h-screen flex items-center justify-center font-display text-2xl">LADE GRUPPEN...</div>;

  return (
    <div className="min-h-screen relative">
      <Toaster theme="dark" position="top-right" richColors />
      {/* Header */}
      <header className="sticky top-0 z-40 dc-surface dc-border border-b backdrop-blur-md" style={{ backgroundColor: "rgba(18,24,21,0.85)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
            {logo ? (
              <img src={logo} alt="Club" className="w-10 h-10 object-contain" data-testid="brand-logo-custom" />
            ) : (
              <div className="relative w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--dc-primary)" }}>
                <div className="absolute inset-1.5 rounded-full bull-pulse" style={{ background: "var(--dc-bg)" }} />
                <Target className="relative z-10 text-white" size={18} />
              </div>
            )}
            <div className="leading-none">
              <div className="font-display text-2xl tracking-wider">{activeClub.name}</div>
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "var(--dc-muted)" }}>Liga Manager</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {visibleNavLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `dc-tab flex items-center gap-2 ${isActive ? "active" : ""}`
                }
              >
                <l.icon size={14} />
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <select value={activeClub.id} onChange={changeClub} className="dc-input text-sm py-2 max-w-48" data-testid="club-selector" aria-label="Gruppe auswählen">
              {clubs.map((club) => <option key={club.id} value={club.id}>{club.name}</option>)}
            </select>
            {admin && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest" style={{ background: "var(--dc-secondary)", color: "white" }} data-testid="admin-badge">
                Admin
              </span>
            )}
            {admin && (
              <button onClick={logout} className="dc-btn dc-btn-ghost text-sm" data-testid="admin-logout-button">
                <LogOut size={14} /> Logout
              </button>
            )}
          </div>
        </div>
        {/* mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-1 px-4 pb-2 border-t dc-border">
          {visibleNavLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} className={({ isActive }) => `dc-tab whitespace-nowrap text-sm ${isActive ? "active" : ""}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-8">
        {children}
      </main>

      <footer className="max-w-7xl mx-auto px-4 md:px-6 py-8 border-t dc-border mt-16 text-xs" style={{ color: "var(--dc-muted)" }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-display tracking-widest">AISSEN180 · {activeClub.name} · Best of 3 · Hin & Rückrunde</div>
          <div className="font-mono-data">SPECIAL RULE · Ausbullen erlaubt</div>
        </div>
      </footer>
    </div>
  );
}
